import { ChevronRightIcon } from 'lucide-react';
import { useState, type JSX } from 'react';

import { fetchArtifactText } from '@/lib/api/quality.js';
import type { ApiCallSummary } from '@/lib/api/quality-types.js';
import { cn } from '@/lib/utils.js';

/** The line above a step's API calls (MEDA-914). */
export const API_CALLS_LINE = 'API calls made during this step:';

/** The record keeps only the headers the step wrote, so the client's own defaults never appear. */
const NO_REQUEST_HEADERS =
  '(none set by the test step; the HTTP client adds its default headers, which are not listed)';
const NO_RESPONSE_HEADERS = '(no headers)';
const NO_BODY = '(no body)';

/**
 * The API calls one step made, one line each. A line expands to the request and the response
 * preview the report already carries; when the preview was cut, "Show full body" loads the full
 * masked record from the run's artifacts. The worker masked every record before storing it, so
 * nothing here hides or reveals anything. Renders nothing when the step made no calls, and also
 * for a value that is not an array: the report is cast from the API, not validated.
 */
export function ApiCallEvidence({
  runId,
  calls,
}: {
  runId: string;
  calls?: ApiCallSummary[];
}): JSX.Element | null {
  if (!Array.isArray(calls) || calls.length === 0) return null;
  return (
    <div className="mt-1.5 space-y-1 pl-3.5">
      <p className="text-xs text-muted-foreground/80">{API_CALLS_LINE}</p>
      {calls.map((call) => (
        <ApiCallCard key={call.id} runId={runId} call={call} />
      ))}
    </div>
  );
}

/** What the full record adds over the summary: headers and the untruncated bodies. */
interface FullRecord {
  requestHeaders: Record<string, unknown>;
  requestBody: string;
  responseHeaders: Record<string, unknown>;
  responseBody: string;
  bodyTruncated: boolean;
  bodyBytes: number;
}

type FullRecordState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'loaded'; record: FullRecord };

function ApiCallCard({
  runId,
  call,
}: {
  runId: string;
  call: ApiCallSummary;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [full, setFull] = useState<FullRecordState>({ kind: 'idle' });
  const canLoadFull =
    (call.request_body_preview_truncated ||
      call.body_preview_truncated ||
      call.body_truncated) &&
    full.kind !== 'loaded';

  const loadFull = async (): Promise<void> => {
    setFull({ kind: 'loading' });
    try {
      const text = await fetchArtifactText(runId, call.artifact_key);
      setFull(
        text === null
          ? { kind: 'failed' }
          : { kind: 'loaded', record: parseFullRecord(text) },
      );
    } catch {
      setFull({ kind: 'failed' });
    }
  };

  return (
    <div className="rounded-md border border-border bg-muted/30 text-xs">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full min-w-0 items-center gap-1.5 px-2 py-1 text-left font-mono"
      >
        <ChevronRightIcon
          className={cn(
            'size-3 shrink-0 transition-transform',
            open && 'rotate-90',
          )}
          aria-hidden
        />
        <span className="shrink-0 font-semibold">{call.method}</span>
        <span className="shrink-0 text-muted-foreground">{call.host}</span>
        <span
          className="min-w-0 truncate text-muted-foreground"
          title={call.path}
        >
          {call.path}
        </span>
        <span className="ml-auto shrink-0 font-semibold text-foreground">
          {outcomeLabel(call)}
        </span>
        {typeof call.duration_ms === 'number' ? (
          <span className="shrink-0 tabular-nums text-muted-foreground/60">
            {call.duration_ms} ms
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="space-y-1.5 border-t border-border px-2 py-1.5">
          <p className="text-muted-foreground/70">
            Entry {call.id} in the API log
          </p>
          {full.kind === 'loaded' ? (
            <BodyBlock
              title="Request headers"
              text={headerText(full.record.requestHeaders)}
              empty={NO_REQUEST_HEADERS}
            />
          ) : null}
          {full.kind === 'loaded' ? (
            <BodyBlock
              title="Request body"
              text={prettyBody(full.record.requestBody)}
            />
          ) : (
            <BodyBlock
              title="Request body"
              text={prettyBody(call.request_body_preview)}
              cut={call.request_body_preview_truncated}
            />
          )}
          {call.status === null ? null : full.kind === 'loaded' ? (
            <>
              <BodyBlock
                title="Response headers"
                text={headerText(full.record.responseHeaders)}
                empty={NO_RESPONSE_HEADERS}
              />
              <BodyBlock
                title="Response body"
                text={prettyBody(full.record.responseBody)}
              />
              {full.record.bodyTruncated ? (
                <p className="text-muted-foreground/70">
                  Body truncated. The response was{' '}
                  {full.record.bodyBytes.toLocaleString('en-US')} bytes; the
                  first 1 MB was kept.
                </p>
              ) : null}
            </>
          ) : (
            <BodyBlock
              title="Response body"
              text={prettyBody(call.body_preview)}
              cut={call.body_preview_truncated}
            />
          )}
          {canLoadFull ? (
            <button
              type="button"
              disabled={full.kind === 'loading'}
              onClick={() => void loadFull()}
              className="rounded border border-border px-1.5 py-0.5 text-foreground transition-colors hover:border-foreground/30 disabled:opacity-60"
            >
              Show full body
            </button>
          ) : null}
          {full.kind === 'failed' ? (
            <p className="text-destructive">
              The full record could not be loaded.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function BodyBlock({
  title,
  text,
  cut = false,
  empty = NO_BODY,
}: {
  title: string;
  text: string;
  cut?: boolean;
  /** What an empty section says; a body by default. */
  empty?: string;
}): JSX.Element {
  return (
    <div>
      <p className="text-muted-foreground/70">{title}</p>
      <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words rounded bg-background/60 p-1.5 font-mono">
        {text || empty}
        {cut ? '\n…' : ''}
      </pre>
    </div>
  );
}

/** `201`, or `no response (timeout)` when the call got none. */
function outcomeLabel(call: ApiCallSummary): string {
  if (typeof call.status === 'number') return String(call.status);
  return `no response (${call.error || 'unknown error'})`;
}

/** A body pretty-printed when it is a whole JSON document; otherwise as written. */
function prettyBody(text: string): string {
  try {
    const parsed: unknown = JSON.parse(text);
    if (parsed !== null && typeof parsed === 'object')
      return JSON.stringify(parsed, null, 2);
  } catch {
    return text;
  }
  return text;
}

function headerText(headers: Record<string, unknown>): string {
  return Object.entries(headers)
    .map(([name, value]) => `${name}: ${String(value)}`)
    .join('\n');
}

function parseFullRecord(text: string): FullRecord {
  const record = asRecord(JSON.parse(text) as unknown);
  const request = asRecord(record.request);
  const response = asRecord(record.response);
  return {
    requestHeaders: asRecord(request.headers),
    requestBody: typeof request.body === 'string' ? request.body : '',
    responseHeaders: asRecord(response.headers),
    responseBody: typeof response.body === 'string' ? response.body : '',
    bodyTruncated: response.body_truncated === true,
    bodyBytes: Number(response.body_bytes ?? 0),
  };
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}
